import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ListActionBar from 'src/components/ListActionBar';
import InfoAlert from 'src/components/InfoAlert';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import { useColumnVisibility } from 'src/hooks/columnVisibility';
import TableActionButton from 'src/components/TableActionButton';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableHeadTypography from 'src/components/TableHeadTypography';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import DeviceTagChip from 'src/components/DeviceTagChip';
import StatusChip from 'src/components/StatusChip';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import { pagesText } from 'src/constants/pagesText';
import {
  useExportWdacReadinessDevices,
  useInfiniteWdacReadinessDevices,
  useWdacReadinessStatusCounts,
} from 'src/hooks/device';
import { useTagOptions } from 'src/hooks/tag';
import { formatDate, formatRelativeDays } from 'src/utils/formatTime';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { DEVICE_STATUS, getDeviceStatusLabel } from 'src/utils/enums/deviceStatus';
import {
  HEALTH_STATUS,
  getHealthStatusColor,
  getHealthStatusLabel,
} from 'src/utils/enums/healthStatus';
import {
  READINESS_STATUS,
  getReadinessStatusColor,
  getReadinessStatusCountKey,
  getReadinessStatusLabel,
} from 'src/utils/enums/readinessStatus';
import { WDAC_STATUS } from 'src/utils/enums/wdacStatus';

const READINESS_TABLE_COLUMNS = [
  {
    key: 'name',
    label: 'Device Name',
    sortable: true,
    sortKey: 'name',
    required: true,
    width: '220px',
  },
  {
    key: 'checkInCount',
    label: 'Check-In Count',
    sortable: true,
    sortKey: 'cleanCheckInCount',
    required: true,
    width: '330px',
  },
  { key: 'readinessStatus', label: 'Readiness Status', required: true, width: '180px' },
  { key: 'lastCheckIn', label: 'Last Check-In', required: true, width: '180px' },
  { key: 'healthStatus', label: 'Health Status', width: '150px' },
  { key: 'deviceStatus', label: 'Device Status', width: '140px' },
  { key: 'tags', label: 'Device Tags', width: '240px' },
  { key: 'note', label: 'Note', width: '72px' },
];

const DEFAULT_VISIBLE_COLUMNS = ['name', 'checkInCount', 'readinessStatus', 'lastCheckIn'];
const READINESS_WARNING_MESSAGE =
  'This device should be reviewed because it is Revoked/Archived or not Healthy, which signals it may not be ready for enforcement.';
const DEFAULT_SORT = { column: 'cleanCheckInCount', direction: 'desc' };
const DEFAULT_FILTERS = {
  statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
  healthStatuses: [HEALTH_STATUS.Healthy],
  readinessStatus: '',
  tagId: '',
};

const StatCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  flex: 1,
  minWidth: 0,
  cursor: 'pointer',
  borderRadius: 8,
  border: `1px solid ${active ? '#87C8E0' : '#5B5B5B'}`,
  backgroundColor: active ? '#3E4E55' : '#4B4B4B',
  padding: '14px 18px',
}));

const WdacReadinessTable = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS) ?? DEFAULT_FILTERS,
  );
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { visibleColumnDefinitions, isColumnVisible, setColumnVisible, resetColumns } =
    useColumnVisibility('wdacReadiness', READINESS_TABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS);

  useFilterParams(filters);
  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);
  const { data: tagOptionsData } = useTagOptions({ search: '' });

  const countsQuery = useWdacReadinessStatusCounts({
    apiQuery: {
      Statuses: filters.statuses,
      HealthStatuses: filters.healthStatuses,
      WdacStatus: WDAC_STATUS.Audit,
      ...(filters.tagId ? { TagIds: [Number(filters.tagId)] } : {}),
    },
  });

  const apiQuery = useMemo(
    () => ({
      ...(trimmedSearchValue ? { Search: trimmedSearchValue } : {}),
      ...(filters.statuses.length ? { Statuses: filters.statuses } : {}),
      ...(filters.healthStatuses.length ? { HealthStatuses: filters.healthStatuses } : {}),
      ...(filters.readinessStatus ? { Status: filters.readinessStatus } : {}),
      ...(filters.tagId ? { TagIds: [Number(filters.tagId)] } : {}),
      SortColumn: sort.column,
      SortDesc: sort.direction === 'desc',
      WdacStatus: WDAC_STATUS.Audit,
    }),
    [filters, sort, trimmedSearchValue],
  );

  const readinessDevicesQuery = useInfiniteWdacReadinessDevices({
    apiQuery,
    limit: 50,
  });
  const exportDevicesMutation = useExportWdacReadinessDevices();

  const readinessDevices = useMemo(
    () => readinessDevicesQuery.data?.pages?.flatMap(page => page.devices || []) || [],
    [readinessDevicesQuery.data],
  );
  const totalCount = readinessDevicesQuery.data?.pages?.[0]?.count ?? 0;

  const loadMore = useCallback(() => {
    if (readinessDevicesQuery.hasNextPage) {
      readinessDevicesQuery.fetchNextPage();
    }
  }, [readinessDevicesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: readinessDevicesQuery.isFetchingNextPage,
    hasNextPage: readinessDevicesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleRefresh = useCallback(async () => {
    await Promise.all([readinessDevicesQuery.refetch(), countsQuery.refetch()]);
    enqueueSnackbar('WDAC readiness list successfully refreshed.', {
      variant: 'success',
      autoHideDuration: 3000,
    });
  }, [countsQuery, enqueueSnackbar, readinessDevicesQuery]);

  const handleExport = useCallback(async () => {
    try {
      const data = await exportDevicesMutation.mutateAsync({ query: apiQuery });

      if (!data?.fileContents) {
        enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      const fileData = atob(data.fileContents);
      const byteArray = new Uint8Array(fileData.length);
      for (let index = 0; index < fileData.length; index += 1) {
        byteArray[index] = fileData.charCodeAt(index);
      }

      const blob = new Blob([byteArray], { type: data.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.fileDownloadName || 'devices-export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error) {
      enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [apiQuery, enqueueSnackbar, exportDevicesMutation]);

  const handleFilterChange = useCallback(nextFilters => {
    setFilters({
      statuses: nextFilters.statuses || [],
      healthStatuses: nextFilters.healthStatuses || [],
      readinessStatus: nextFilters.readinessStatus || '',
      tagId: nextFilters.tagId || '',
    });
  }, []);

  const handleStatusCardClick = useCallback(statusKey => {
    setFilters(currentFilters => ({
      ...currentFilters,
      readinessStatus: currentFilters.readinessStatus === statusKey ? '' : statusKey,
    }));
  }, []);

  const handleRowClick = useCallback(
    deviceId => {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(DEVICES_PREVIOUS_PATH, returnPath);
      navigate(`${PORTAL_ROUTE}/devices/${deviceId}/general`);
    },
    [navigate],
  );

  const handleOpenEnvironmentSettings = useCallback(() => {
    navigate(`${PORTAL_ROUTE}/environments/status-settings`);
  }, [navigate]);

  const handleSort = useCallback(column => {
    if (!column.sortable || !column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column: column.sortKey, direction: 'desc' },
    );
  }, []);

  const filterOptions = useMemo(
    () => [
      {
        key: 'statuses',
        type: 'checkbox',
        label: 'Device Status',
        options: [
          { value: DEVICE_STATUS.Approved, label: getDeviceStatusLabel(DEVICE_STATUS.Approved) },
          { value: DEVICE_STATUS.Revoked, label: getDeviceStatusLabel(DEVICE_STATUS.Revoked) },
          { value: DEVICE_STATUS.Archived, label: getDeviceStatusLabel(DEVICE_STATUS.Archived) },
          { value: DEVICE_STATUS.Unknown, label: getDeviceStatusLabel(DEVICE_STATUS.Unknown) },
        ],
      },
      {
        key: 'healthStatuses',
        type: 'checkbox',
        label: 'Health Status',
        options: [
          { value: HEALTH_STATUS.Healthy, label: getHealthStatusLabel(HEALTH_STATUS.Healthy) },
          {
            value: HEALTH_STATUS.ConfigIssue,
            label: getHealthStatusLabel(HEALTH_STATUS.ConfigIssue),
          },
          { value: HEALTH_STATUS.Offline, label: getHealthStatusLabel(HEALTH_STATUS.Offline) },
        ],
      },
      {
        key: 'readinessStatus',
        type: 'select',
        label: 'Readiness Status',
        options: [
          { value: '', label: 'All statuses' },
          ...Object.values(READINESS_STATUS).map(value => ({
            value,
            label: getReadinessStatusLabel(value),
          })),
        ],
      },
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All Tags' },
          ...(tagOptionsData?.tags || []).map(tag => ({
            value: String(tag.id),
            label: tag.name,
          })),
        ],
      },
    ],
    [tagOptionsData],
  );

  const showReadinessWarning = useCallback(device => {
    const isNonNeedsWork = device.lastReadinessStatus !== READINESS_STATUS.NeedsWork;
    const hasProblemStatus =
      device.status === DEVICE_STATUS.Archived || device.status === DEVICE_STATUS.Revoked;
    const isNotHealthy = device.healthStatus !== HEALTH_STATUS.Healthy;

    return isNonNeedsWork && (hasProblemStatus || isNotHealthy);
  }, []);

  const renderCell = useCallback(
    (column, device) => {
      switch (column.key) {
        case 'name':
          return (
            <TableCell key={`${device.id}-name`} sx={{ py: 0.75, width: column.width }}>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}>
                {device.name}
              </Typography>
            </TableCell>
          );
        case 'checkInCount':
          return (
            <TableCell key={`${device.id}-checkins`} sx={{ py: 0.75, width: column.width }}>
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {device.cleanCheckInCount} Clean Check-Ins
                </Typography>
                <Typography variant="caption" sx={{ color: '#DDDDDD' }}>
                  In last 30 days:
                </Typography>
                <Typography variant="caption" sx={{ color: '#DDDDDD' }}>
                  {device.win32MatchedAppCountLast30Days} Win32 Matched apps, last seen{' '}
                  {formatRelativeDays(device.win32MatchedLastEventDate)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#DDDDDD' }}>
                  {device.win32UnmatchedFileCountLast30Days} Win32 Unmatched files, last seen{' '}
                  {formatRelativeDays(device.win32UnmatchedLastEventDate)}
                </Typography>
              </Stack>
            </TableCell>
          );
        case 'readinessStatus': {
          const statusColor = getReadinessStatusColor(device.lastReadinessStatus);

          return (
            <TableCell key={`${device.id}-readiness`} sx={{ py: 0.75, width: column.width }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <StatusChip
                  label={getReadinessStatusLabel(device.lastReadinessStatus)}
                  color={statusColor}
                  backgroundColor={statusColor}
                  textColor="#fff"
                />
                {showReadinessWarning(device) ? (
                  <Tooltip title={READINESS_WARNING_MESSAGE}>
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      onClick={event => event.stopPropagation()}>
                      <Iconify
                        icon="material-symbols:warning-outline-rounded"
                        color="#F4B740"
                        width={18}
                        height={18}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
              </Stack>
            </TableCell>
          );
        }
        case 'lastCheckIn':
          return (
            <TableCell key={`${device.id}-lastcheckin`} sx={{ py: 0.75, width: column.width }}>
              <Typography variant="caption">
                {formatDate(device.lastReadinessStatusUpdateDate) || '--'}
              </Typography>
            </TableCell>
          );
        case 'healthStatus': {
          const color = getHealthStatusColor(device.healthStatus);

          return (
            <TableCell key={`${device.id}-health`} sx={{ py: 0.75, width: column.width }}>
              <StatusChip
                label={getHealthStatusLabel(device.healthStatus)}
                color={color}
                backgroundColor={`${color}1F`}
              />
            </TableCell>
          );
        }
        case 'deviceStatus':
          return (
            <TableCell key={`${device.id}-status`} sx={{ py: 0.75, width: column.width }}>
              <Typography variant="caption">{getDeviceStatusLabel(device.status)}</Typography>
            </TableCell>
          );
        case 'tags':
          return (
            <TableCell key={`${device.id}-tags`} sx={{ py: 0.75, width: column.width }}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {(device.tags || []).length ? (
                  (device.tags || []).map(tag => <DeviceTagChip key={tag.id} label={tag.name} />)
                ) : (
                  <Typography variant="body2">No tags</Typography>
                )}
              </Stack>
            </TableCell>
          );
        case 'note':
          return (
            <TableCell
              key={`${device.id}-note`}
              align="center"
              onClick={event => event.stopPropagation()}
              sx={{ py: 0.75, width: column.width }}>
              {device.notes ? (
                <Tooltip
                  title={device.notes}
                  arrow
                  componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F' } } }}>
                  <Box
                    width={20}
                    height={20}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center">
                    <Image
                      src="/static/icons/notes-notepad.svg"
                      alt="Notes"
                      width={18}
                      height={18}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  --
                </Typography>
              )}
            </TableCell>
          );
        default:
          return null;
      }
    },
    [showReadinessWarning],
  );

  const ReadinessColumnHeader = ({ column }) => (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      onClick={column.sortable ? () => handleSort(column) : undefined}
      sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sort.column === column.sortKey}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {column.label}
      </TableHeadTypography>
      {column.sortable ? (
        <Iconify
          icon={
            sort.column === column.sortKey
              ? sort.direction === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sort.column === column.sortKey ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );

  return (
    <Box sx={{ position: 'relative', mt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <InfoAlert>
          <Typography variant="body2" sx={{ color: 'inherit', fontSize: '0.8125rem' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              How to read this:
            </Box>{' '}
            Audited devices are sorted by clean check-in count (consecutive check-ins with nothing
            new to resolve). Higher counts indicate devices are closer to enforcement mode. The app
            and file counts show what is still outstanding from the last 30 days: apps you have not
            marked untrusted, and unmatched files you have not ignored. Readiness status is
            configured from your environment settings, which you can manage{' '}
            <Link
              component="button"
              type="button"
              onClick={handleOpenEnvironmentSettings}
              underline="always"
              sx={{
                fontWeight: 700,
                color: '#BEEBFF',
                textDecorationColor: '#BEEBFF',
              }}>
              here
            </Link>
            .
          </Typography>
        </InfoAlert>
      </Box>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Search devices"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={3}
        filterButtonCount={totalCount}
        actionArea={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Refresh">
              <span>
                <TableActionButton
                  variant="outlined"
                  onClick={handleRefresh}
                  disabled={readinessDevicesQuery.isRefetching || countsQuery.isFetching}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    px: 0.75,
                  }}>
                  <Iconify icon="material-symbols:refresh" width={18} height={18} />
                </TableActionButton>
              </span>
            </Tooltip>
            <ColumnVisibilityMenu
              columns={READINESS_TABLE_COLUMNS}
              isVisible={isColumnVisible}
              onChange={setColumnVisible}
              onReset={resetColumns}
            />
            <TableActionButton
              variant="outlined"
              onClick={handleExport}
              disabled={exportDevicesMutation.isLoading}
              sx={{ px: 1.5 }}>
              {exportDevicesMutation.isLoading ? 'Exporting...' : 'Export All'}
            </TableActionButton>
          </Stack>
        }
        contentArea={
          <Stack direction="row" gap={2} sx={{ width: '100%' }}>
            {Object.values(READINESS_STATUS).map(statusKey => {
              const countKey = getReadinessStatusCountKey(statusKey);

              return (
                <StatCard
                  key={statusKey}
                  active={filters.readinessStatus === statusKey}
                  onClick={() => handleStatusCardClick(statusKey)}>
                  <Typography variant="body2" sx={{ color: '#DDDDDD', mb: 1 }}>
                    {getReadinessStatusLabel(statusKey)}
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                    {countsQuery.isLoading ? '-' : countsQuery.data?.[countKey] || 0}
                  </Typography>
                </StatCard>
              );
            })}
          </Stack>
        }
        contentAreaContainerProps={{
          sx: {
            mt: 1,
            mb: 1,
          },
        }}
      />

      <TableContainer
        roundedTop
        styles={{
          maxHeight: 'calc(100vh - 340px)',
        }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              fontSize: '0.8125rem',
            },
          }}>
          <colgroup>
            {visibleColumnDefinitions.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {visibleColumnDefinitions.map(column => (
                <TableCell key={column.key} sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                  <ReadinessColumnHeader column={column} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: '#4B4B4B',
              '& .MuiTableRow-root': {
                height: 90,
              },
            }}>
            {readinessDevicesQuery.isLoading ? (
              <LoadingRows columnCount={visibleColumnDefinitions.length} rowCount={6} />
            ) : (
              <>
                {readinessDevices.map((device, index) => (
                  <TableRow
                    key={device.id}
                    ref={index === readinessDevices.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(device.id)}
                    sx={{ cursor: 'pointer' }}>
                    {visibleColumnDefinitions.map(column => renderCell(column, device))}
                  </TableRow>
                ))}

                {readinessDevicesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={visibleColumnDefinitions.length} rowCount={3} />
                ) : null}

                {!readinessDevicesQuery.isLoading && readinessDevices.length === 0 ? (
                  <NoDataCell colSpan={visibleColumnDefinitions.length}>
                    {pagesText.noDataFound}
                  </NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WdacReadinessTable;
